import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../hooks/useAuthContext'
import Navbar from '../components/Navbar'
import { axiosInstance } from '../context/AuthContext'
import { FaFacebook, FaTwitter, FaLinkedin, FaLink } from 'react-icons/fa'

const BlogPage = () => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [blogs, setBlogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const itemsPerPage = 10
  const [message, setMessage] = useState('')

  useEffect(() => {
    fetchBlogs()
  }, [page])

  const handleShare = (platform, blog) => {
    const url = `${window.location.origin}/blog/${blog._id}`
    const title = blog.title
    const text = blog.excerpt || blog.title

    let shareUrl = ''
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        break
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
        break
      case 'link':
        navigator.clipboard.writeText(url)
        setMessage('Link copied to clipboard!')
        setTimeout(() => setMessage(''), 3000)
        return
      default:
        return
    }
    window.open(shareUrl, '_blank', 'width=600,height=400')
  }

  const fetchBlogs = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        status: '1'
      })

      const response = await axiosInstance.get(`/blog?${params.toString()}`)
      setBlogs(response.data.blogs || [])
      setTotalPages(response.data.totalPages || 1)
    } catch (error) {
      console.error('Error fetching blogs:', error)
    } finally {
      setIsLoading(false)
    }
  }


  return (
    <div className="flex min-h-screen bg-white">
      {user && <Navbar />}
      <main className={`flex-1 mx-auto p-6 max-w-5xl mx-auto`}>
        <div className="text-center mb-12 mt-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">Artization Blog</h1>
        </div>

        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">{message}</div>
        )}

        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Loading blog posts...</div>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">No blog posts available</p>
            <p>Check back soon for updates!</p>
          </div>
        ) : (
          <div className="space-y-12">
            {blogs.map((blog, index) => {
                  const isFirstPost = index === 0
                  const blogDate = new Date(blog.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })

                  return (
                    <article
                      key={blog._id}
                    >
                      {isFirstPost ? (
                        <div className="flex flex-col">
                          <div className="flex items-center justify-between mb-4">
                            <time className="text-gray-600 text-sm font-medium">
                              {blogDate}
                            </time>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleShare('facebook', blog)}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                aria-label="Share on Facebook"
                                title="Share on Facebook"
                              >
                                <FaFacebook className="text-sm" />
                              </button>
                              <button
                                onClick={() => handleShare('twitter', blog)}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-black hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                aria-label="Share on X"
                                title="Share on X"
                              >
                                <FaTwitter className="text-sm" />
                              </button>
                              <button
                                onClick={() => handleShare('linkedin', blog)}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#0077B5] hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                aria-label="Share on LinkedIn"
                                title="Share on LinkedIn"
                              >
                                <FaLinkedin className="text-sm" />
                              </button>
                              <button
                                onClick={() => handleShare('link', blog)}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                                aria-label="Copy link"
                                title="Copy link"
                              >
                                <FaLink className="text-xs" />
                              </button>
                            </div>
                          </div>

                          <h2
                            className="font-bold text-gray-900 mb-6 cursor-pointer hover:text-blue-600 transition-colors text-3xl md:text-4xl"
                            onClick={() => navigate(`/blog/${blog._id}`)}
                          >
                            {blog.title}
                          </h2>

                          {blog.featuredImage ? (
                            <div
                              className="rounded-2xl overflow-hidden mb-6 w-full aspect-[4/3]"
                              style={{
                                backgroundImage: `url(${blog.featuredImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#f0f0f0'
                              }}
                            >
                              <img
                                src={blog.featuredImage}
                                alt={blog.title}
                                className="w-full aspect-[4/3] object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                            </div>
                          ) : (
                            <div className="rounded-2xl bg-gray-100 h-[400px] md:h-[500px] flex items-center justify-center mb-6">
                              <div className="text-gray-400 text-center">
                                <p>No image</p>
                              </div>
                            </div>
                          )}

                          {blog.excerpt && (
                            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                              {blog.excerpt}
                            </p>
                          )}

                          <button
                            onClick={() => navigate(`/blog/${blog._id}`)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-lg flex items-center gap-2 transition-colors self-start"
                          >
                            Learn more
                            <span>&gt;</span>
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                          <div className="order-2 md:order-1 md:col-span-1">
                            {blog.featuredImage ? (
                              <div
                                className="rounded-lg overflow-hidden w-full aspect-[4/3]"
                                style={{
                                  backgroundImage: `url(${blog.featuredImage})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundColor: '#f0f0f0'
                                }}
                              >
                                <img
                                  src={blog.featuredImage}
                                  alt={blog.title}
                                  className="w-full aspect-[4/3] object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="rounded-2xl bg-gray-100 h-[180px] md:h-[250px] flex items-center justify-center">
                                <div className="text-gray-400 text-center">
                                  <p>No image</p>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="order-1 md:order-2 md:col-span-2 flex flex-col">
                            <div className="flex items-center justify-between mb-4">
                              <time className="text-gray-600 text-sm font-medium">
                                {blogDate}
                              </time>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleShare('facebook', blog)}
                                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                  aria-label="Share on Facebook"
                                  title="Share on Facebook"
                                >
                                  <FaFacebook className="text-sm" />
                                </button>
                                <button
                                  onClick={() => handleShare('twitter', blog)}
                                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-black hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                  aria-label="Share on X"
                                  title="Share on X"
                                >
                                  <FaTwitter className="text-sm" />
                                </button>
                                <button
                                  onClick={() => handleShare('linkedin', blog)}
                                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#0077B5] hover:text-white flex items-center justify-center transition-colors text-gray-600"
                                  aria-label="Share on LinkedIn"
                                  title="Share on LinkedIn"
                                >
                                  <FaLinkedin className="text-sm" />
                                </button>
                                <button
                                  onClick={() => handleShare('link', blog)}
                                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                                  aria-label="Copy link"
                                  title="Copy link"
                                >
                                  <FaLink className="text-xs" />
                                </button>
                              </div>
                            </div>

                            <h2
                              className="font-bold text-gray-900 mb-6 cursor-pointer hover:text-blue-600 transition-colors text-3xl md:text-4xl"
                              onClick={() => navigate(`/blog/${blog._id}`)}
                            >
                              {blog.title}
                            </h2>

                            {blog.excerpt && (
                              <p className="text-gray-700 text-lg mb-6 leading-relaxed flex-grow">
                                {blog.excerpt}
                              </p>
                            )}

                            <button
                              onClick={() => navigate(`/blog/${blog._id}`)}
                              className="text-blue-600 hover:text-blue-700 font-medium text-lg flex items-center gap-2 transition-colors self-start"
                            >
                              Learn more
                              <span>&gt;</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  )
                })}
              </div>
            )}

        {totalPages > 1 && (
          <div className="mt-12 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              Previous
            </button>
            <span className="px-4 py-2 flex items-center">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  )
}

export default BlogPage

